// =============================================================================
// plannerSettings.ts — load/save the planner offset rules (v0.2.34)
// =============================================================================
//
// The offset rules live at households/{hid}/settings/planner_offsets. Older
// households created before v0.2.34 won't have the doc; loadPlannerOffsets
// falls back to DEFAULT_PLANNER_OFFSETS so the planner always works.
// =============================================================================

import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import type { Firestore } from 'firebase/firestore';
import type { PlannerOffsets } from '@scholarship-app/types';
import { DEFAULT_PLANNER_OFFSETS } from './planner';

export type PlannerOffsetValues = Omit<PlannerOffsets, 'updated_at' | 'updated_by'>;

/** Load the household's planner offsets, falling back to defaults. */
export async function loadPlannerOffsets(
  db: Firestore,
  householdId: string
): Promise<PlannerOffsetValues> {
  try {
    const ref = doc(db, 'households', householdId, 'settings', 'planner_offsets');
    const snap = await getDoc(ref);
    if (!snap.exists()) return { ...DEFAULT_PLANNER_OFFSETS };
    const d = snap.data() as Partial<PlannerOffsets>;
    // Merge over defaults so a partial/older doc still yields every field.
    return {
      essay_draft_days_before:
        d.essay_draft_days_before ?? DEFAULT_PLANNER_OFFSETS.essay_draft_days_before,
      essay_final_days_before:
        d.essay_final_days_before ?? DEFAULT_PLANNER_OFFSETS.essay_final_days_before,
      recommendation_request_days_before:
        d.recommendation_request_days_before ??
        DEFAULT_PLANNER_OFFSETS.recommendation_request_days_before,
      transcript_request_days_before:
        d.transcript_request_days_before ??
        DEFAULT_PLANNER_OFFSETS.transcript_request_days_before,
      test_scores_send_days_before:
        d.test_scores_send_days_before ??
        DEFAULT_PLANNER_OFFSETS.test_scores_send_days_before,
      portfolio_days_before:
        d.portfolio_days_before ?? DEFAULT_PLANNER_OFFSETS.portfolio_days_before,
    };
  } catch {
    return { ...DEFAULT_PLANNER_OFFSETS };
  }
}

/** Save the household's planner offsets. */
export async function savePlannerOffsets(
  db: Firestore,
  householdId: string,
  userId: string,
  values: PlannerOffsetValues
): Promise<void> {
  const ref = doc(db, 'households', householdId, 'settings', 'planner_offsets');
  await setDoc(ref, {
    ...values,
    updated_at: serverTimestamp(),
    updated_by: userId,
  });
}
